import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import clsx from 'clsx';
import { ApiError } from '../../lib/api.js';
import {
  useAddMember,
  useMembers,
  useRemoveMember,
  type MemberListItem,
  type MemberRole,
} from './use-members.js';

const ADDABLE_ROLES: MemberRole[] = ['company_admin', 'company_hr', 'company_viewer'];

export function MembersPage(): JSX.Element {
  const { companyId } = useParams<{ companyId: string }>();
  const { data, isLoading, error } = useMembers(companyId);
  const addMember = useAddMember(companyId);
  const removeMember = useRemoveMember(companyId);

  const [showAdd, setShowAdd] = useState(false);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<MemberRole>('company_hr');

  const liveAdminCount = useMemo(
    () => (data?.items ?? []).filter((m) => m.roles.includes('company_admin')).length,
    [data],
  );

  if (isLoading) return <p className="text-slate-500">Loading members…</p>;
  if (error) {
    return (
      <div className="rounded-md border border-red-300 bg-red-50 p-4 text-red-800">
        <p className="font-medium">Could not load members.</p>
        <p className="text-sm">{error.message}</p>
      </div>
    );
  }

  const items = data?.items ?? [];

  const resetAddForm = (): void => {
    setEmail('');
    setRole('company_hr');
    addMember.reset();
  };

  const addErr = addMember.error instanceof ApiError ? addMember.error : null;
  const addErrIsUserNotFound = addErr?.code === 'user_not_found';

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Members</h1>
          <p className="mt-1 text-sm text-slate-600">
            Users with at least one active role in this company. Add a member by their Entra email —
            they must sign in to the portal once first.
          </p>
        </div>
        <div className="flex items-center gap-3 text-sm">
          <Link to="/" className="text-brand-600 hover:text-brand-700">
            ← Back
          </Link>
          <button
            type="button"
            onClick={() => {
              setShowAdd((v) => !v);
              if (showAdd) resetAddForm();
            }}
            className="rounded-md border border-slate-300 px-3 py-1 font-medium hover:bg-slate-50"
          >
            {showAdd ? 'Cancel' : '+ Add member'}
          </button>
        </div>
      </div>

      {showAdd && (
        <form
          className="mb-6 rounded-lg border border-slate-200 bg-white p-4"
          onSubmit={(e) => {
            e.preventDefault();
            addMember.mutate(
              { email: email.trim(), role },
              {
                onSuccess: () => {
                  setShowAdd(false);
                  resetAddForm();
                },
              },
            );
          }}
        >
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[240px]">
              <label className="block text-sm font-medium text-slate-800">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="user@company.com"
                className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-800">Role</label>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value as MemberRole)}
                className="mt-1 block rounded-md border border-slate-300 px-3 py-2 text-sm"
              >
                {ADDABLE_ROLES.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </div>
            <button
              type="submit"
              disabled={addMember.isPending || email.trim() === ''}
              className={clsx(
                'rounded-md px-4 py-2 text-sm font-medium text-white shadow-sm',
                addMember.isPending || email.trim() === ''
                  ? 'bg-slate-300 cursor-not-allowed'
                  : 'bg-brand-600 hover:bg-brand-700',
              )}
            >
              {addMember.isPending ? 'Adding…' : 'Add'}
            </button>
          </div>
          {addErr && (
            <div
              className={clsx(
                'mt-3 rounded border px-3 py-2 text-sm',
                addErrIsUserNotFound
                  ? 'border-amber-300 bg-amber-50 text-amber-900'
                  : 'border-red-300 bg-red-50 text-red-800',
              )}
            >
              {addErr.message}
            </div>
          )}
        </form>
      )}

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-2 font-medium">Name</th>
              <th className="px-4 py-2 font-medium">Email</th>
              <th className="px-4 py-2 font-medium">Roles</th>
              <th className="px-4 py-2 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {items.length === 0 && (
              <tr>
                <td className="px-4 py-6 text-center text-slate-500" colSpan={4}>
                  No members yet. Click <span className="font-medium">Add member</span> to grant a
                  role.
                </td>
              </tr>
            )}
            {items.map((m) => (
              <MemberRow
                key={m.userId}
                member={m}
                liveAdminCount={liveAdminCount}
                onRemove={(userId, role) => {
                  if (
                    !window.confirm(
                      `Remove ${role} from ${m.displayName || m.email}? They'll lose access immediately.`,
                    )
                  ) {
                    return;
                  }
                  removeMember.mutate({ userId, role });
                }}
                removing={removeMember.isPending}
              />
            ))}
          </tbody>
        </table>
      </div>
      {removeMember.error && (
        <div className="mt-4 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-800">
          {removeMember.error.message}
        </div>
      )}
    </div>
  );
}

function MemberRow(props: {
  member: MemberListItem;
  liveAdminCount: number;
  onRemove: (userId: string, role: MemberRole) => void;
  removing: boolean;
}): JSX.Element {
  const { member, liveAdminCount, onRemove, removing } = props;
  return (
    <tr>
      <td className="px-4 py-3 font-medium text-slate-900">{member.displayName}</td>
      <td className="px-4 py-3 text-slate-700">{member.email}</td>
      <td className="px-4 py-3">
        <div className="flex flex-wrap gap-1">
          {member.roles.map((r) => (
            <span
              key={r}
              className="inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-700"
            >
              {r}
            </span>
          ))}
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="flex flex-wrap gap-1">
          {member.roles.map((r) => {
            const isLastAdmin = r === 'company_admin' && liveAdminCount <= 1;
            return (
              <button
                key={r}
                type="button"
                disabled={removing || isLastAdmin}
                onClick={() => onRemove(member.userId, r)}
                title={
                  isLastAdmin
                    ? 'Cannot remove the last company_admin. Grant another admin first.'
                    : `Remove ${r}`
                }
                className={clsx(
                  'rounded border px-2 py-0.5 text-xs',
                  isLastAdmin || removing
                    ? 'cursor-not-allowed border-slate-200 text-slate-400'
                    : 'border-slate-300 text-slate-700 hover:bg-slate-50',
                )}
              >
                Remove {r}
              </button>
            );
          })}
        </div>
      </td>
    </tr>
  );
}
